/**
 * ProtectedRoute Component
 *
 * Wraps protected pages and redirects to login if not authenticated.
 * Also supports role-based access control (buyer vs seller).
 */

import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

export default function ProtectedRoute({
  children,
  requiredRole = null // 'buyer', 'seller', or null for any authenticated user
}) {
  const { isAuthenticated, userProfile, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  // For admin, check both role and user_type
  const isAdmin = userProfile?.role === 'admin' || userProfile?.user_type === 'admin';
  const effectiveUserType = isAdmin ? 'admin' : userProfile?.user_type;
  
  if (requiredRole && effectiveUserType !== requiredRole) {
    // Redirect to appropriate dashboard
    if (isAdmin) {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (effectiveUserType === 'buyer') {
      return <Navigate to="/buyer/search" replace />;
    } else if (effectiveUserType === 'seller') {
      return <Navigate to="/seller/dashboard" replace />;
    }
    // Fallback
    return <Navigate to="/" replace />;
  }

  return children;
}

// Buyer-only route wrapper
export function BuyerRoute({ children }) {
  return <ProtectedRoute requiredRole="buyer">{children}</ProtectedRoute>;
}

// Seller-only route wrapper
export function SellerRoute({ children }) {
  return <ProtectedRoute requiredRole="seller">{children}</ProtectedRoute>;
}

// Admin-only route wrapper
export function AdminRoute({ children }) {
  return <ProtectedRoute requiredRole="admin">{children}</ProtectedRoute>;
}

// Route that blocks sellers (for public browse pages - sellers shouldn't see competitors)
export function NotSellerRoute({ children }) {
  const { userProfile, loading } = useAuth();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is a seller, redirect to their dashboard
  if (userProfile?.user_type === 'seller') {
    return <Navigate to="/seller/dashboard" replace />;
  }

  // Allow everyone else (unauthenticated, buyers, admins)
  return children;
}
